import {
  ClipboardDocumentIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { Skeleton } from 'boneyard-js/react'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { ColumnVisibilityToggle } from '../../../components/ColumnVisibilityToggle'
import { UnsavedChangesDialog } from '../../../components/UnsavedChangesDialog'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DatePicker } from '../../../components/ui/date-picker'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  type ColumnVisibilityDef,
  useColumnVisibility,
} from '../../../hooks/useColumnVisibility'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges'
import { supabase } from '../../../lib/supabase'
import type {
  CreateProfileFormValues,
  ProfileFormValues,
  ProfileRow,
} from './interfaces/Profile'

const PAGE_SIZE = 20

const HIDDEN_COLS_KEY = 'profiles-hidden-cols'

const COLUMN_DEFS: ColumnVisibilityDef[] = [
  { id: 'first_name', label: 'Name', alwaysVisible: true },
  { id: 'id', label: 'User ID', hiddenByDefault: true },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
  { id: 'birthdate', label: 'Birthdate' },
  { id: 'role', label: 'Role' },
  { id: 'id_card_number', label: 'ID Card' },
]

type SortColumn =
  | 'id'
  | 'first_name'
  | 'email'
  | 'phone'
  | 'birthdate'
  | 'role'
  | 'id_card_number'

interface SortState {
  column: SortColumn
  ascending: boolean
}

const profileSchema = z.object({
  first_name: z.string().optional().nullable(),
  last_name: z.string().optional().nullable(),
  birthdate: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  id_card_number: z.string().optional().nullable(),
  role: z.enum(['admin', 'user']),
})

const createProfileSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('manual'),
    email: z.string().optional(),
    first_name: z.string().optional().nullable(),
    last_name: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    birthdate: z.string().optional().nullable(),
    id_card_number: z.string().optional().nullable(),
    role: z.enum(['admin', 'user']),
  }),
  z.object({
    mode: z.literal('invite'),
    email: z.string().email('Invalid email address'),
    first_name: z.string().optional().nullable(),
    last_name: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    birthdate: z.string().optional().nullable(),
    id_card_number: z.string().optional().nullable(),
    role: z.enum(['admin', 'user']),
  }),
])

const rowToForm = (row: ProfileRow): ProfileFormValues => ({
  first_name: row.first_name ?? '',
  last_name: row.last_name ?? '',
  birthdate: row.birthdate ?? '',
  phone: row.phone ?? '',
  id_card_number: row.id_card_number ?? '',
  role: row.role as 'admin' | 'user',
})

const emptyEditForm = (): ProfileFormValues => ({
  first_name: '',
  last_name: '',
  birthdate: '',
  phone: '',
  id_card_number: '',
  role: 'user',
})

const emptyCreateForm = (): CreateProfileFormValues => ({
  mode: 'manual',
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  birthdate: '',
  id_card_number: '',
  role: 'user',
})

export default function ProfilesPage() {
  const queryClient = useQueryClient()
  const [editingProfile, setEditingProfile] = useState<ProfileRow | null>(null)
  const [form, setForm] = useState<ProfileFormValues>(emptyEditForm())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [copied, setCopied] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createForm, setCreateForm] = useState<CreateProfileFormValues>(
    emptyCreateForm(),
  )
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({})
  const [showDeleted, setShowDeleted] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [profileSearch, setProfileSearch] = useState('')
  const [sortState, setSortState] = useState<SortState>({
    column: 'first_name',
    ascending: true,
  })
  const [searchParams] = useSearchParams()
  const showHeader = searchParams.get('header') !== 'hide'
  const debouncedProfileSearch = useDebouncedValue(profileSearch, 500)
  const normalizedProfileSearch = debouncedProfileSearch.trim()

  const handleSort = (column: SortColumn) => {
    setSortState((prev) =>
      prev.column === column
        ? { column, ascending: !prev.ascending }
        : { column, ascending: true },
    )
  }

  const sortIndicator = (column: SortColumn) => {
    if (sortState.column !== column) return ' ⇅'
    return sortState.ascending ? ' ↑' : ' ↓'
  }

  const {
    ref: colsDropdownRef,
    open: colsOpen,
    setOpen: setColsOpen,
    hiddenCount,
    isVisible,
    toggleColumn,
  } = useColumnVisibility(HIDDEN_COLS_KEY, COLUMN_DEFS)

  const col = isVisible

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        'profiles',
        showDeleted,
        sortState.column,
        sortState.ascending,
        normalizedProfileSearch,
      ],
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const from = pageParam
        const to = from + PAGE_SIZE - 1
        const searchFilters: string[] = []

        if (normalizedProfileSearch) {
          const { data: contractRows, error: contractError } = await supabase
            .from('contracts')
            .select('tenant_id')
            .eq('room_number', normalizedProfileSearch)
            .is('deleted_at', null)
          if (contractError) throw contractError

          const tenantIds = Array.from(
            new Set((contractRows ?? []).map((row) => row.tenant_id)),
          )
          const [firstNameSearch, ...lastNameParts] =
            normalizedProfileSearch.split(/\s+/)
          const lastNameSearch = lastNameParts.join(' ')

          searchFilters.push(
            `first_name.eq.${normalizedProfileSearch}`,
            `last_name.eq.${normalizedProfileSearch}`,
          )

          if (firstNameSearch && lastNameSearch) {
            searchFilters.push(
              `and(first_name.eq.${firstNameSearch},last_name.eq.${lastNameSearch})`,
            )
          }

          if (tenantIds.length > 0) {
            searchFilters.push(`id.in.(${tenantIds.join(',')})`)
          }
        }

        let query = supabase
          .from('profiles_with_email')
          .select('*')
          .order(sortState.column, { ascending: sortState.ascending })

        if (searchFilters.length > 0) {
          query = query.or(searchFilters.join(','))
        }

        const { data, error } = showDeleted
          ? await query.not('deleted_at', 'is', null).range(from, to)
          : await query.is('deleted_at', null).range(from, to)
        if (error) throw error
        return data as ProfileRow[]
      },
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPage.length < PAGE_SIZE ? undefined : lastPageParam + PAGE_SIZE,
    })

  const profiles: ProfileRow[] = data?.pages.flat() ?? []

  const onLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const sentinelRef = useInfiniteScroll(onLoadMore, Boolean(hasNextPage))

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id: string
      values: ProfileFormValues
    }) => {
      const parsed = profileSchema.parse(values)
      const payload = {
        ...parsed,
        first_name: parsed.first_name || null,
        last_name: parsed.last_name || null,
        birthdate: parsed.birthdate || null,
        phone: parsed.phone || null,
        id_card_number: parsed.id_card_number || null,
      }
      const { error } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] })
      setEditingProfile(null)
      setForm(emptyEditForm())
      setErrors({})
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('profiles')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] })
      setDeleteTarget(null)
    },
  })

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('profiles')
        .update({ deleted_at: null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] })
      setRestoreError(null)
    },
    onError: (err: Error) => {
      setRestoreError(`Restore failed: ${err.message}`)
    },
  })

  const createMutation = useMutation({
    mutationFn: async (values: CreateProfileFormValues) => {
      const payload =
        values.mode === 'manual'
          ? {
              mode: 'manual' as const,
              first_name: values.first_name || null,
              last_name: values.last_name || null,
              phone: values.phone || null,
              birthdate: values.birthdate || null,
              id_card_number: values.id_card_number || null,
              role: values.role,
            }
          : {
              mode: 'invite' as const,
              email: values.email,
              first_name: values.first_name || null,
              last_name: values.last_name || null,
              role: values.role,
            }
      const { data, error } = await supabase.functions.invoke(
        'admin-create-profile',
        { body: payload },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] })
      setShowCreateModal(false)
      setCreateForm(emptyCreateForm())
      setCreateErrors({})
    },
  })

  const openCreate = () => {
    setCreateForm(emptyCreateForm())
    setCreateErrors({})
    setShowCreateModal(true)
  }

  const closeCreate = () => {
    setShowCreateModal(false)
    setCreateForm(emptyCreateForm())
    setCreateErrors({})
  }

  const isCreateDirty = useMemo((): boolean => {
    if (!showCreateModal) return false
    const empty = emptyCreateForm()
    return (
      createForm.email !== empty.email ||
      createForm.first_name !== empty.first_name ||
      createForm.last_name !== empty.last_name ||
      createForm.phone !== empty.phone ||
      createForm.birthdate !== empty.birthdate ||
      createForm.id_card_number !== empty.id_card_number ||
      createForm.role !== empty.role
    )
  }, [showCreateModal, createForm])

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const result = createProfileSchema.safeParse(createForm)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        if (issue.path[0]) fieldErrors[String(issue.path[0])] = issue.message
      }
      setCreateErrors(fieldErrors)
      return
    }
    setCreateErrors({})
    createMutation.mutate(createForm)
  }

  const openEdit = (profile: ProfileRow) => {
    setEditingProfile(profile)
    setForm(rowToForm(profile))
    setErrors({})
  }

  const closeForm = () => {
    setEditingProfile(null)
    setForm(emptyEditForm())
    setErrors({})
  }

  const isEditDirty = useMemo((): boolean => {
    if (!editingProfile) return false
    const orig = rowToForm(editingProfile)
    return (
      form.first_name !== orig.first_name ||
      form.last_name !== orig.last_name ||
      form.birthdate !== orig.birthdate ||
      form.phone !== orig.phone ||
      form.id_card_number !== orig.id_card_number ||
      form.role !== orig.role
    )
  }, [editingProfile, form])

  const isProfilesDirty = isEditDirty || isCreateDirty
  const { isBlocked, proceed, reset } = useUnsavedChanges(isProfilesDirty)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false)

  const handleCancelEdit = (): void => {
    if (isEditDirty) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const handleCancelCreate = (): void => {
    if (isCreateDirty) {
      setShowDiscardConfirm(true)
    } else {
      closeCreate()
    }
  }

  const handleConfirmDiscard = (): void => {
    setShowDiscardConfirm(false)
    if (editingProfile) {
      closeForm()
    }
    if (showCreateModal) {
      closeCreate()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingProfile) return
    const result = profileSchema.safeParse(form)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        if (issue.path[0]) fieldErrors[String(issue.path[0])] = issue.message
      }
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    updateMutation.mutate({ id: editingProfile.id, values: form })
  }

  function renderHeader() {
    return (
      <>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-gray-900 text-lg">Profiles</h2>
            <p className="text-gray-500 text-xs">
              {profiles.length} profile{profiles.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={openCreate} className="flex items-center gap-1.5">
            <UserPlusIcon className="h-4 w-4" />
            Add Profile
          </Button>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-xs">
          <ColumnVisibilityToggle
            title="Columns"
            columns={COLUMN_DEFS}
            isOpen={colsOpen}
            hiddenCount={hiddenCount}
            wrapperRef={colsDropdownRef}
            isVisible={isVisible}
            onToggle={toggleColumn}
            onToggleOpen={() => setColsOpen((o) => !o)}
          />
          <div className="flex items-center gap-2">
            <Label htmlFor="room-search" className="text-xs">
              Search:
            </Label>
            <Input
              id="room-search"
              type="search"
              placeholder="Exact name or room"
              value={profileSearch}
              onChange={(e) => setProfileSearch(e.target.value)}
              className="h-7 w-36 text-xs"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowDeleted((v) => !v)
              setRestoreError(null)
            }}
            className={
              showDeleted ? 'border-red-300 bg-red-50 text-red-700' : ''
            }
          >
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </Button>
        </div>

        {restoreError && (
          <div className="mb-3 rounded-md bg-red-50 px-4 py-2 text-red-700 text-sm">
            {restoreError}
            <button
              type="button"
              onClick={() => setRestoreError(null)}
              className="ml-2 font-medium underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="relative flex h-full flex-col">
      {showHeader && (
        <div className="sticky top-0 z-30 bg-gray-50">{renderHeader()}</div>
      )}

      <Skeleton
        name="admin-profiles"
        loading={isLoading}
        className="flex min-h-0 flex-1 flex-col *:data-boneyard-content:flex *:data-boneyard-content:min-h-0 *:data-boneyard-content:flex-1 *:data-boneyard-content:flex-col"
      >
        <Card className="min-h-0 flex-1 overflow-hidden p-0">
          <div className="h-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="sticky top-0 z-20 bg-gray-50">
                  {(
                    [
                      { key: 'first_name', label: 'Name', align: 'left' },
                      { key: 'id', label: 'User ID', align: 'left' },
                      { key: 'email', label: 'Email', align: 'left' },
                      { key: 'phone', label: 'Phone', align: 'left' },
                      { key: 'birthdate', label: 'Birthdate', align: 'left' },
                      { key: 'role', label: 'Role', align: 'left' },
                      {
                        key: 'id_card_number',
                        label: 'ID Card',
                        align: 'left',
                      },
                    ] as {
                      key: SortColumn
                      label: string
                      align: 'left' | 'right'
                    }[]
                  )
                    .filter(({ key }) => col(key))
                    .map(({ key, label, align }) => (
                      <th
                        key={key}
                        className={`px-4 py-3 font-medium text-gray-500 text-${align}`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(key)}
                          className={`inline-flex cursor-pointer items-center gap-0.5 hover:text-gray-900 ${
                            sortState.column === key ? 'text-gray-900' : ''
                          }`}
                        >
                          {label}
                          <span className="text-xs">{sortIndicator(key)}</span>
                        </button>
                      </th>
                    ))}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {profiles.map((profile) => (
                  <tr
                    key={profile.id}
                    className={
                      profile.deleted_at
                        ? 'bg-red-50 opacity-60'
                        : 'hover:bg-gray-50'
                    }
                  >
                    <td
                      className={`px-4 py-3 font-medium ${
                        profile.deleted_at ? 'text-gray-400 line-through' : ''
                      }`}
                    >
                      {profile.first_name} {profile.last_name}
                    </td>
                    {col('id') && (
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-gray-600 text-xs">
                        {profile.id}
                      </td>
                    )}
                    {col('email') && (
                      <td className="break-all px-4 py-3">
                        {profile.email ?? '—'}
                      </td>
                    )}
                    {col('phone') && (
                      <td className="px-4 py-3">{profile.phone ?? '—'}</td>
                    )}
                    {col('birthdate') && (
                      <td className="px-4 py-3">{profile.birthdate ?? '—'}</td>
                    )}
                    {col('role') && (
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 font-semibold text-xs ${
                            profile.role === 'admin'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {profile.role}
                        </span>
                      </td>
                    )}
                    {col('id_card_number') && (
                      <td className="px-4 py-3 font-mono text-xs">
                        {profile.id_card_number ?? '—'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      {profile.deleted_at ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => restoreMutation.mutate(profile.id)}
                          disabled={restoreMutation.isPending}
                          className="text-green-700 hover:bg-green-50"
                        >
                          Restore
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(profile)}
                            className="mr-2"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteTarget(profile.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div ref={sentinelRef} className="h-6" />
          {isFetchingNextPage && (
            <div className="py-2 text-center text-gray-500">Loading more…</div>
          )}
        </Card>
      </Skeleton>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-lg p-6">
            <h3 className="mb-4 font-semibold text-lg">Add Profile</h3>

            {/* Mode toggle */}
            <div className="mb-5 flex rounded-lg border border-gray-200 p-1">
              {(['manual', 'invite'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() =>
                    setCreateForm({ ...emptyCreateForm(), mode: m })
                  }
                  className={`flex-1 rounded-md py-1.5 font-medium text-sm transition-colors ${
                    createForm.mode === m
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {m === 'manual' ? 'Manual Entry' : 'Invite User'}
                </button>
              ))}
            </div>

            {createForm.mode === 'invite' && (
              <p className="mb-4 rounded-md bg-blue-50 px-3 py-2 text-blue-700 text-sm">
                An invite email with a magic link will be sent to the address
                below. The user sets their password on first login.
              </p>
            )}

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              {createForm.mode === 'invite' && (
                <div>
                  <Label htmlFor="create_email">Email *</Label>
                  <Input
                    id="create_email"
                    type="email"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, email: e.target.value })
                    }
                  />
                  {createErrors.email && (
                    <p className="mt-1 text-red-500 text-xs">
                      {createErrors.email}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="create_first_name">First Name</Label>
                  <Input
                    id="create_first_name"
                    value={createForm.first_name}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        first_name: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="create_last_name">Last Name</Label>
                  <Input
                    id="create_last_name"
                    value={createForm.last_name}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        last_name: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              {createForm.mode === 'manual' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="create_phone">Phone</Label>
                      <Input
                        id="create_phone"
                        value={createForm.phone}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            phone: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="create_birthdate">Birthdate</Label>
                      <DatePicker
                        id="create_birthdate"
                        value={createForm.birthdate}
                        onValueChange={(value) =>
                          setCreateForm({ ...createForm, birthdate: value })
                        }
                        className="mt-1 w-full text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="create_id_card_number">
                      ID Card Number
                    </Label>
                    <Input
                      id="create_id_card_number"
                      value={createForm.id_card_number}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          id_card_number: e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="create_role">Role</Label>
                <select
                  id="create_role"
                  title="Role"
                  value={createForm.role}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      role: e.target.value as 'admin' | 'user',
                    })
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {createMutation.error && (
                <p className="text-red-500 text-sm">
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : 'Failed to create profile.'}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelCreate}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending
                    ? 'Saving…'
                    : createForm.mode === 'invite'
                      ? 'Send Invite'
                      : 'Create Profile'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="mb-2 font-semibold text-lg">Delete Profile</h3>
            <p className="mb-6 text-gray-600 text-sm">
              Are you sure you want to archive this profile? You can restore it
              later from the deleted profiles view.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => deleteMutation.mutate(deleteTarget)}
                disabled={deleteMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                Archive
              </Button>
            </div>
          </Card>
        </div>
      )}

      {editingProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-md p-6">
            <h3 className="mb-1 font-semibold text-lg">Edit Profile</h3>
            <div className="mb-4">
              <Label className="text-gray-500 text-xs">Supabase User ID</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 select-all overflow-x-auto rounded bg-gray-100 px-2 py-1 font-mono text-gray-700 text-xs">
                  {editingProfile.id}
                </code>
                <button
                  type="button"
                  title="Copy User ID"
                  className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  onClick={() => {
                    navigator.clipboard.writeText(editingProfile.id)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 1500)
                  }}
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              </div>
              {copied && (
                <p className="mt-0.5 text-green-600 text-xs">Copied!</p>
              )}
            </div>
            <div className="mb-4">
              <Label className="text-gray-500 text-xs">Email</Label>
              <div className="mt-1 rounded bg-gray-100 px-3 py-2 text-gray-700 text-sm">
                {editingProfile.email ?? '—'}
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={form.first_name}
                    onChange={(e) =>
                      setForm({ ...form, first_name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={form.last_name}
                    onChange={(e) =>
                      setForm({ ...form, last_name: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="birthdate">Birthdate</Label>
                  <DatePicker
                    id="birthdate"
                    value={form.birthdate}
                    onValueChange={(value) =>
                      setForm({ ...form, birthdate: value })
                    }
                    className="mt-1 w-full text-sm"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="id_card_number">ID Card Number</Label>
                <Input
                  id="id_card_number"
                  value={form.id_card_number}
                  onChange={(e) =>
                    setForm({ ...form, id_card_number: e.target.value })
                  }
                />
                {errors.id_card_number && (
                  <p className="mt-1 text-red-500 text-xs">
                    {errors.id_card_number}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  title="Role"
                  value={form.role}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      role: e.target.value as 'admin' | 'user',
                    })
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
                {errors.role && (
                  <p className="mt-1 text-red-500 text-xs">{errors.role}</p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  Save Changes
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <UnsavedChangesDialog
        isOpen={isBlocked}
        title="Unsaved Changes"
        description="You have unsaved profile changes. Are you sure you want to leave without saving? Your changes will be lost."
        confirmText="Leave without saving"
        cancelText="Stay on page"
        onConfirm={proceed}
        onCancel={reset}
      />

      <UnsavedChangesDialog
        isOpen={showDiscardConfirm}
        title="Discard Profile Changes?"
        description="You have unsaved changes in this form. Are you sure you want to discard them?"
        confirmText="Discard changes"
        cancelText="Keep editing"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  )
}
